// The environment's shared folder (D84): GET /api/environments/{id}/shared lists what the owner
// put in /workspaces/shared, which agents read at /shared. 409 while the environment is stopped.
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas } from "../../api/client";
import { ApiError, unwrap } from "../../api/queries";
import { formatNumber } from "../../i18n";

export type SharedFiles = Schemas["SharedFilesDto"];
export type SharedEntry = Schemas["SharedEntryDto"];

export const sharedFilesQuery = (environmentId: string) =>
  queryOptions({
    queryKey: ["environment-shared", environmentId],
    queryFn: () =>
      unwrap(api.GET("/api/environments/{environment_id}/shared", { params: { path: { environment_id: environmentId } } })),
    // A stopped environment answers 409 until a task starts it; asking again changes nothing.
    retry: (failures, error) => !isStopped(error) && failures < 2,
  });

export const isStopped = (error: unknown): boolean => error instanceof ApiError && error.status === 409;

const UNITS = ["B", "KB", "MB", "GB", "TB"];

/** 2478497280 → "2.3 GB", 512 → "512 B" (powers of 1024, one decimal above bytes). */
export function fileSize(bytes: number): string {
  let n = bytes;
  let unit = 0;
  while (n >= 1024 && unit < UNITS.length - 1) {
    n /= 1024;
    unit += 1;
  }
  return `${formatNumber(n, { maximumFractionDigits: unit === 0 ? 0 : 1 })} ${UNITS[unit]}`;
}
